import { useMemo, useState } from "react";
import { HiCheck, HiSearch } from "react-icons/hi";
import ChatAvatar from "./ChatAvatar";
import { useChat } from "./ChatProvider";

// Searchable multi-select of people. `exclude` hides ids (e.g. yourself or existing members).
function MemberPicker({ selected, onChange, exclude = [] }) {
  const { users } = useChat();
  const [query, setQuery] = useState("");

  const people = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.values(users)
      .filter((u) => !exclude.includes(u.id))
      .filter((u) => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, query, exclude]);

  const toggle = (id) =>
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  return (
    <div>
      <div className="relative mb-2">
        <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people"
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
      </div>
      {selected.length > 0 && (
        <p className="text-xs text-gray-500 mb-2">{selected.length} selected</p>
      )}
      <div className="max-h-64 overflow-y-auto -mx-1">
        {people.length === 0 && <p className="text-sm text-gray-500 px-1 py-3">No people found.</p>}
        {people.map((user) => {
          const checked = selected.includes(user.id);
          return (
            <button
              type="button"
              key={user.id}
              onClick={() => toggle(user.id)}
              className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-left"
              aria-pressed={checked}
            >
              <ChatAvatar name={user.name} src={user.avatarThumbUrl} seed={user.id} size="sm" online={user.online} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">{user.name}</span>
                <span className="block text-xs text-gray-500 truncate">{user.email}</span>
              </span>
              <span
                className={`w-5 h-5 rounded border flex items-center justify-center ${
                  checked ? "bg-[#2470B7] border-[#2470B7] text-white" : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {checked && <HiCheck className="w-4 h-4" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default MemberPicker;
